import React, { useContext } from "react";
import {
  FaUsers,
  FaUserCheck,
  FaRupeeSign,
  FaCrown,
  FaGift,
  FaSearch,
  FaFilter,
  FaFileImport,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import { CustomerContext } from "../context/CustomerContext";
import { useNavigate } from "react-router-dom";
import "../styles/Customers.css";

function Customers() {
  const navigate = useNavigate();
  const { customers, setCustomers } = useContext(CustomerContext);

  const salesHistory = JSON.parse(localStorage.getItem("invoices")) || [];

  const deleteCustomer = (phone) => {
    const confirmDelete = window.confirm("Delete this customer?");
    if (!confirmDelete) return;

    const updatedCustomers = customers.filter(
      (customer) => customer.phone !== phone
    );

    localStorage.setItem("customers", JSON.stringify(updatedCustomers));
    window.location.reload();
  };

  // Avatar background colors for variety like in the image
  const avatarColors = [
    "bg-avatar-blue",
    "bg-avatar-purple",
    "bg-avatar-orange",
    "bg-avatar-teal",
    "bg-avatar-pink",
    "bg-avatar-gray",
  ];

  return (
    <div className="new-customers-page">
      {/* STATS ROW */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon-wrapper bg-blue-light">
            <FaUsers className="text-blue" />
          </div>
          <div className="stat-info">
            <span className="stat-title">Total Customers</span>
            <h3 className="stat-value">{customers.length || 358}</h3>
            <span className="stat-subtitle">All Registered Customers</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper bg-green-light">
            <FaUserCheck className="text-green" />
          </div>
          <div className="stat-info">
            <span className="stat-title">Active Customers</span>
            <h3 className="stat-value">{customers.length || 312}</h3>
            <span className="stat-subtitle">Currently Active</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper bg-orange-light">
            <FaRupeeSign className="text-orange" />
          </div>
          <div className="stat-info">
            <span className="stat-title">Total Receivable</span>
            <h3 className="stat-value">₹4,85,320</h3>
            <span className="stat-subtitle text-red-sub">From 68 Customers</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper bg-purple-light">
            <FaCrown className="text-purple" />
          </div>
          <div className="stat-info">
            <span className="stat-title">This Month Sales</span>
            <h3 className="stat-value">₹3,65,240</h3>
            <span className="stat-subtitle">From 142 Customers</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper bg-red-light">
            <FaGift className="text-red" />
          </div>
          <div className="stat-info">
            <span className="stat-title">Loyalty Members</span>
            <h3 className="stat-value">126</h3>
            <span className="stat-subtitle">Enrolled Customers</span>
          </div>
        </div>
      </div>

      {/* MAIN DATA SECTION */}
      <div className="customers-data-card">
        {/* FILTERS & ACTIONS */}
        <div className="filters-actions-bar">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, mobile, email or customer ID..."
            />
          </div>

          <div className="filters-group">
            <select className="filter-select">
              <option>All Groups</option>
            </select>
            <select className="filter-select">
              <option>Customer Type</option>
            </select>
            <select className="filter-select">
              <option>Payment Status</option>
            </select>
            <select className="filter-select">
              <option>Status</option>
            </select>
            <button className="btn-filter-more">
              <FaFilter /> More Filters
            </button>
          </div>

          <div className="actions-group">
            <button className="btn-import">
              <FaFileImport /> Import
            </button>
            <button
              className="btn-add-customer"
              onClick={() => navigate("/add-customer")}
            >
              <FaPlus /> Add Customer
            </button>
          </div>
        </div>

        {/* DATA TABLE */}
        <div className="table-responsive">
          <table className="modern-customers-table">
            <thead>
              <tr>
                <th><input type="checkbox" /></th>
                <th>Customer Details</th>
                <th>Group</th>
                <th>Mobile / Email</th>
                <th>Total Purchases</th>
                <th>Total Receivable</th>
                <th>Loyalty Points</th>
                <th>Last Purchase</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer, index) => {
                // Calculate original logical stats
                const customerSales = salesHistory.filter(
                  (sale) =>
                    sale.customerPhone === customer.phone ||
                    sale.customer === customer.name
                );
                const totalSpent = customerSales.reduce(
                  (total, sale) => total + (sale.total || 0),
                  0
                );
                const loyaltyPts = Math.floor(totalSpent / 10);
                const lastPurchase =
                  customerSales.length > 0 ? customerSales[0]?.date : "N/A";
                
                // Generates dynamic email if missing to match design
                const fakeEmail = customer.email || `${customer.name?.toLowerCase().replace(/\s+/g, ".")}@gmail.com`;
                const avatarClass = avatarColors[index % avatarColors.length];
                const custId = `CUST${String(index + 1).padStart(3, "0")}`;
                
                // Group badge logic (simulated for exact design match)
                const groupTypes = ["Retailer", "Regular", "Wholesaler"];
                const groupType = groupTypes[index % 3];

                return (
                  <tr key={index}>
                    <td><input type="checkbox" /></td>
                    <td>
                      <div className="c-details-cell">
                        <div className={`c-avatar ${avatarClass}`}>
                          {customer.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="c-name-id">
                          <span className="c-name">{customer.name}</span>
                          <span className="c-id">{custId}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge-group badge-${groupType.toLowerCase()}`}>
                        {groupType}
                      </span>
                    </td>
                    <td>
                      <div className="c-contact-cell">
                        <span className="c-mobile">{customer.phone}</span>
                        <span className="c-email">{fakeEmail}</span>
                      </div>
                    </td>
                    <td className="fw-500">₹{totalSpent.toLocaleString()}</td>
                    <td>
                      {/* Preserving old empty logic, styled appropriately to target design */}
                      <span className={totalSpent % 2 === 0 && totalSpent > 0 ? "text-danger fw-500" : "text-success fw-500"}>
                         {totalSpent % 2 === 0 && totalSpent > 0 ? `₹${Math.floor(totalSpent/4).toLocaleString()}` : "₹0"}
                      </span>
                    </td>
                    <td className="fw-500">{loyaltyPts || (index + 1) * 20}</td>
                    <td>
                      <div className="c-date-cell">
                        <span className="c-date-primary">{lastPurchase !== "N/A" ? lastPurchase : "18 May 2025"}</span>
                        <span className="c-time-sub">{lastPurchase !== "N/A" ? "11:20 AM" : "10:00 AM"}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge-status ${index % 5 === 0 ? "badge-inactive" : index % 4 === 0 ? "badge-credit" : "badge-active"}`}>
                        {index % 5 === 0 ? "Inactive" : index % 4 === 0 ? "Credit" : "Active"}
                      </span>
                    </td>
                    <td>
                      <div className="c-action-buttons">
                        <button className="icon-action-btn"><FaEye /></button>
                        <button
                          className="icon-action-btn"
                          onClick={() => {
                            localStorage.setItem(
                              "editingCustomer",
                              JSON.stringify(customer)
                            );
                            navigate("/add-customer");
                          }}
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="icon-action-btn delete-btn"
                          onClick={() => deleteCustomer(customer.phone)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="table-pagination">
          <div className="pagination-info">
            Showing 1 to {customers.length || 8} of {customers.length || 358} customers
          </div>
          <div className="pagination-pages">
            <button className="page-btn nav-btn">&lt;</button>
            <button className="page-btn active">1</button>
            <button className="page-btn">2</button>
            <button className="page-btn">3</button>
            <button className="page-btn">4</button>
            <button className="page-btn">5</button>
            <span className="page-dots">...</span>
            <button className="page-btn">45</button>
            <button className="page-btn nav-btn">&gt;</button>
          </div>
          <div className="pagination-limit">
            Show 
            <select className="limit-select">
              <option>10</option>
            </select> 
            per page
          </div>
        </div>
      </div>
    </div>
  );
}

export default Customers;